import React, { useState } from "react";
import { FileWarning, CheckCircle2, ArrowRight, FileCheck2, MessageCircle, X } from "lucide-react";
import RelancePartner from "./RelancePartner";
import RelancePiecesButton from "./RelancePiecesButton";
import HoverPreview from "./HoverPreview";
import { useGallery } from "./AttachmentStrip";

export const OPEN_SLOT_EVENT = "norea:open-doc-slot";
export const PENDING_SLOT_KEY = "norea_pending_doc_slot";

export const openDocSlot = (label) => {
  sessionStorage.setItem(PENDING_SLOT_KEY, label);
  window.dispatchEvent(new CustomEvent(OPEN_SLOT_EVENT, { detail: { label } }));
};

function Popup({ title, onClose, children, testid }) {
  React.useEffect(() => {
    const k = (e) => e.key === "Escape" && !document.querySelector("[data-testid=pdf-viewer-modal], [role=dialog]") && onClose();
    window.addEventListener("keydown", k);
    return () => window.removeEventListener("keydown", k);
  }, [onClose]);
  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-start justify-center p-4 md:p-8 overflow-y-auto" onClick={onClose}>
      <div className="bg-norea-bg rounded-xl shadow-2xl w-full max-w-4xl border border-norea-border" onClick={(e) => e.stopPropagation()} data-testid={testid}>
        <div className="px-5 py-3 border-b border-norea-border bg-white rounded-t-xl flex items-center justify-between sticky top-0 z-10">
          <div className="font-heading text-lg font-medium">{title}</div>
          <button onClick={onClose} className="p-1 rounded hover:bg-norea-surfaceHover" data-testid={`${testid}-close`}><X className="w-4 h-4" /></button>
        </div>
        <div className="p-5">{children}</div>
      </div>
    </div>
  );
}

const SmallBtn = ({ icon: Icon, label, onClick, cls, testid }) => (
  <button onClick={onClick} className={`mt-3 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md border text-xs font-semibold transition-colors ${cls}`} title={label} data-testid={testid}>
    <Icon className="w-3.5 h-3.5" /> {label}
  </button>
);

export default function MissingDocsCard({ dossier, onChanged, verifPopup }) {
  const [popup, setPopup] = useState(null);
  const expected = dossier.pieces_attendues || [];
  const missing = dossier.pieces_manquantes || [];
  const received = expected.filter((p) => !missing.includes(p));
  const complete = expected.length > 0 && missing.length === 0;
  return (
    <div className={`card-flat p-5 border ${missing.length ? "border-orange-200 bg-orange-50/40" : "border-emerald-200 bg-emerald-50/40"}`} data-testid="missing-docs-card">
      <div className="flex items-center justify-between gap-2 mb-3">
        <div className="overline flex items-center gap-1.5"><FileWarning className="w-3 h-3" /> Documents manquants</div>
        <button onClick={() => missing[0] && openDocSlot(missing[0])} disabled={!missing.length} className={`text-xs font-semibold ${missing.length ? "text-orange-700 hover:underline" : "text-emerald-700"}`} data-testid="missing-docs-count">
          {expected.length ? `${missing.length} manquant${missing.length > 1 ? "s" : ""} sur ${expected.length} attendus` : "Aucune pièce attendue"}
        </button>
      </div>
      {complete ? (
        <div className="flex items-center gap-1.5 text-sm text-emerald-700" data-testid="missing-docs-complete"><CheckCircle2 className="w-4 h-4" /> Toutes les pièces obligatoires sont reçues</div>
      ) : (
        <div className="flex flex-wrap gap-1.5" data-testid="missing-docs-list">
          {missing.map((p) => (
            <button key={p} onClick={() => openDocSlot(p)} title="Ouvrir dans l'onglet Dossier pour déposer la pièce"
              className="group inline-flex items-center gap-1 rounded-full border border-orange-300 bg-orange-100 text-orange-800 text-xs font-semibold px-2.5 py-1 hover:bg-orange-200 transition-colors" data-testid={`missing-doc-${p}`}>
              {p} <ArrowRight className="w-3 h-3 opacity-0 -ml-1 group-hover:opacity-100 group-hover:ml-0 transition-all" />
            </button>
          ))}
        </div>
      )}
      {(missing.length > 0 || verifPopup) && (
        <div className="flex items-center gap-2 flex-wrap">
          {missing.length > 0 && <RelancePiecesButton dossierId={dossier.id} onSent={onChanged} />}
          {missing.length > 0 && <SmallBtn icon={MessageCircle} label="WhatsApp" onClick={() => setPopup("whatsapp")} cls="border-transparent bg-[#25D366] hover:bg-[#1ebe57] text-white" testid="relance-whatsapp-popup-btn" />}
          {verifPopup && <SmallBtn icon={FileCheck2} label="Vérifier" onClick={() => setPopup("verif")} cls="border-norea-border bg-white hover:bg-norea-surfaceHover text-norea-text" testid="verif-docs-popup-btn" />}
          {dossier.last_relance_at && <span className="mt-3 text-[11px] text-norea-textMuted" data-testid="last-relance">Dernière relance : {new Date(dossier.last_relance_at).toLocaleString("fr-FR", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" })}{dossier.relance_count ? ` · ${dossier.relance_count} au total` : ""}</span>}
        </div>
      )}
      {popup === "verif" && <Popup title="Vérification des pièces" onClose={() => setPopup(null)} testid="verif-docs-popup">{verifPopup}</Popup>}
      {popup === "whatsapp" && (
        <Popup title="Relance WhatsApp" onClose={() => setPopup(null)} testid="whatsapp-popup">
          <RelancePartner dossier={dossier} allowClient onUpdated={() => onChanged?.()} />
        </Popup>
      )}
      {received.length > 0 && <ReceivedList dossierId={dossier.id} received={received} />}
    </div>
  );
}

const norm = (s) => (s || "").toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "").trim();

function ReceivedList({ dossierId, received }) {
  const [items] = useGallery(dossierId, received.join("|"));
  const fileFor = (p) => (items || []).find((f) => norm(f.label) === norm(p)) || (items || []).find((f) => norm(f.label).startsWith(norm(p).slice(0, 8)));
  return (
    <div className="mt-3 text-xs text-emerald-700" data-testid="received-docs-count">
      <div className="flex items-center gap-1 mb-1.5"><CheckCircle2 className="w-3.5 h-3.5" /> Reçues : {received.length}</div>
      <div className="flex flex-wrap gap-1.5" data-testid="received-docs-list">
        {received.map((p, i) => {
          const f = fileFor(p);
          const chip = <span className={`inline-flex items-center gap-1 rounded-full border border-emerald-200 bg-white px-2 py-0.5 text-[11px] ${f ? "cursor-zoom-in hover:bg-emerald-50" : ""}`} data-testid={`received-doc-${i}`}>{p}</span>;
          return f ? <HoverPreview key={p} dossierId={dossierId} file={f} className="inline-flex" testid={`received-doc-preview-${i}`}>{chip}</HoverPreview> : <React.Fragment key={p}>{chip}</React.Fragment>;
        })}
      </div>
    </div>
  );
}
